import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { getMultipleTeacherRatings, getTeacherLatestFeedback } from './bookingService';
import { getFavoriteTeachers, toggleFavoriteTeacher } from './favoriteService';
import { FaSearch, FaStar, FaHeart, FaRegHeart, FaMapMarkerAlt, FaFilter, FaTimes, FaChevronLeft, FaChevronRight, FaUser, FaCheckCircle, FaBell, FaGraduationCap, FaDollarSign, FaChevronDown, FaLaptop, FaFileAlt, FaBook, FaBullseye, FaCalendarAlt, FaPlay, FaComment, FaBolt, FaGlobe, FaUsers } from 'react-icons/fa';
import { toArabicNumerals, formatCurrency, translateSubject, translateLanguage, translateGrade, transliterateName, transliterateNameSync } from './utils/arabicNumbers';

// Brand colors - Orange theme
const COLORS = {
  light: '#FFF8F1',
  dark: '#1A2341',
  orange: '#FF7A1A',
  orangeHover: '#E86A0A',
  orangeLight: '#FFF5F0',
  blue: '#22305A',
  white: '#FFFFFF',
  gray: '#F5F7FA',
  textSecondary: '#64748B',
  borderLight: '#E5E7EB'
};

// Helper function to capitalize first letter of each word in a name
const capitalizeName = (name) => {
  if (!name) return name;
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const TeacherBrowsePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const outletContext = useOutletContext() || {};
  const { isArabic: contextIsArabic, cachedUserProfile, sidebarVisible } = outletContext;
  const [currentUser, setCurrentUser] = useState(null);
  const [isRTL, setIsRTL] = useState(false);

  // Teacher data
  const [teachers, setTeachers] = useState([]);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [teacherRatings, setTeacherRatings] = useState({});
  const [favoriteTeacherIds, setFavoriteTeacherIds] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const teachersPerPage = 10;

  // Search & Filters
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('recommended');
  const [filters, setFilters] = useState({
    subjects: [],
    grades: [],
    price: [0, 30],
    rating: 'all',
    languages: [],
    gender: null,
    lessonFormat: null,
    favoritesOnly: false
  });

  // UI State
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [profilePopupTeacher, setProfilePopupTeacher] = useState(null);
  const [teacherReviews, setTeacherReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [transliterationVersion, setTransliterationVersion] = useState(0); // Force re-render when names are transliterated

  // Available filter options
  const subjectOptions = ['Math', 'Science', 'English', 'Arabic', 'Physics', 'Chemistry', 'Biology', 'History', 'Geography', 'Computer Science', 'Economics', 'Business'];
  const gradeOptions = ['Elementary', 'Middle School', 'High School', 'University', 'Adult'];
  const languageOptions = ['English', 'Arabic', 'French', 'Spanish', 'German'];
  const sortOptions = [
    { value: 'recommended', label: 'Our top picks', labelAr: 'اختياراتنا' },
    { value: 'price_low', label: 'Price: Low to High', labelAr: 'السعر: من الأقل للأعلى' },
    { value: 'price_high', label: 'Price: High to Low', labelAr: 'السعر: من الأعلى للأقل' },
    { value: 'rating', label: 'Highest Rated', labelAr: 'الأعلى تقييماً' }
  ];

  // Check authentication and load user data
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signin');
        return;
      }

      const { data: student } = await supabase
        .from('students')
        .select('*')
        .eq('email', session.user.email)
        .single();

      if (student) {
        setCurrentUser(student);
        loadFavorites(student.uuid_id);
      }
    };

    checkAuth();
  }, [navigate]);

  // Check language preference
  useEffect(() => {
    const urlPath = window.location.pathname;
    setIsRTL(urlPath.includes('-ar') || contextIsArabic);
  }, [contextIsArabic]);

  // Initialize search from URL parameter on mount or when URL changes
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch !== search) {
      setSearch(urlSearch);
    }
  }, [searchParams]);

  // Update URL when search changes (but only if user types, not from URL param)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (search !== urlSearch) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (search.trim()) {
        newSearchParams.set('search', search);
      } else {
        newSearchParams.delete('search');
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [search]); // Only trigger when search state changes, not when URL changes

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch teacher reviews when profile is selected or popup is opened
  useEffect(() => {
    const fetchReviews = async () => {
      const teacher = profilePopupTeacher || selectedTeacher;
      if (teacher && teacher.email) {
        setLoadingReviews(true);
        const { data, error } = await getTeacherLatestFeedback(teacher.email, 5);
        if (!error && data) {
          setTeacherReviews(data);
        } else {
          setTeacherReviews([]);
        }
        setLoadingReviews(false);
      } else {
        setTeacherReviews([]);
      }
    };

    fetchReviews();
  }, [selectedTeacher, profilePopupTeacher]);

  // Load favorite teachers
  const loadFavorites = async (userId) => {
    const { data } = await getFavoriteTeachers(userId);
    if (data) {
      setFavoriteTeacherIds(data.map(fav => fav.teacher_id));
    }
  };

  // Fetch teachers from database
  const fetchTeachers = useCallback(async () => {
    setLoadingTeachers(true);

    try {
      const from = (page - 1) * teachersPerPage;
      const to = from + teachersPerPage - 1;

      let query = supabase
        .from('Teachers')
        .select('*', { count: 'exact' })
        .not('full_name', 'is', null)
        .not('email', 'is', null)
        .not('profile_photo', 'is', null);

      // Apply favorites filter at database level if enabled
      if (filters.favoritesOnly && favoriteTeacherIds.length > 0) {
        query = query.in('uuid_id', favoriteTeacherIds);
      } else if (filters.favoritesOnly && favoriteTeacherIds.length === 0) {
        setTeachers([]);
        setTotalTeachers(0);
        setLoadingTeachers(false);
        return;
      }

      const { data, error, count } = await query
        .range(from, to)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTeachers(data || []);
      setTotalTeachers(count || 0);

      // Fetch ratings for all teachers
      if (data && data.length > 0) {
        const emails = data.map(t => t.email);
        const { data: ratingsData } = await getMultipleTeacherRatings(emails);
        if (ratingsData) {
          setTeacherRatings(ratingsData);
        }
        
        // Pre-transliterate teacher names via API if in Arabic mode and names not in dictionary
        if (isRTL) {
          // Transliterate names asynchronously in background
          Promise.all(
            data
              .filter(teacher => teacher.full_name)
              .map(teacher => transliterateName(teacher.full_name, isRTL, true))
          ).then(() => {
            // Trigger re-render after all names are transliterated
            setTransliterationVersion(prev => prev + 1);
          }).catch(error => {
            console.debug('Some names failed to transliterate:', error);
          });
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return;
      }
      console.error('Error fetching teachers:', error);
    } finally {
      setLoadingTeachers(false);
    }
  }, [page, filters.favoritesOnly, favoriteTeacherIds]);

  // Fetch teachers when page or filters change
  useEffect(() => {
    let isMounted = true;
    let abortController = new AbortController();
    
    const loadTeachers = async () => {
      try {
        await fetchTeachers();
      } catch (error) {
        if (error?.name === 'AbortError' || 
            error?.message?.includes('aborted') ||
            error?.message?.includes('signal')) {
          return;
        }
        if (isMounted) {
          console.error('Error in fetchTeachers effect:', error);
        }
      }
    };

    loadTeachers();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [fetchTeachers]);

  // Client-side filtering and sorting
  const filteredTeachers = useMemo(() => {
    let result = [...teachers];

    // Search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter(teacher =>
        teacher.full_name.toLowerCase().includes(searchLower) ||
        (teacher.subjects && teacher.subjects.some(s => s.toLowerCase().includes(searchLower)))
      );
    }

    // Subject filter
    if (filters.subjects.length > 0) {
      result = result.filter(teacher =>
        teacher.subjects && filters.subjects.some(subject =>
          teacher.subjects.includes(subject)
        )
      );
    }

    // Grade filter
    if (filters.grades.length > 0) {
      result = result.filter(teacher =>
        teacher.grade_levels && filters.grades.some(grade =>
          teacher.grade_levels.includes(grade)
        )
      );
    }

    // Price filter
    result = result.filter(teacher => {
      const displayPrice = teacher.lesson_format_preference === 'group'
        ? teacher.group_hourly_rate
        : teacher.hourly_rate;
      return displayPrice >= filters.price[0] && displayPrice <= filters.price[1];
    });

    // Rating filter
    if (filters.rating !== 'all') {
      const minRating = parseFloat(filters.rating);
      result = result.filter(teacher => {
        const rating = teacherRatings[teacher.email]?.avg_rating || 0;
        return rating >= minRating;
      });
    }

    // Language filter
    if (filters.languages.length > 0) {
      result = result.filter(teacher =>
        teacher.languages && filters.languages.some(lang =>
          teacher.languages.includes(lang)
        )
      );
    }

    // Gender filter
    if (filters.gender) {
      result = result.filter(teacher =>
        teacher.gender && teacher.gender.toLowerCase() === filters.gender.toLowerCase()
      );
    }

    // Lesson format filter
    if (filters.lessonFormat) {
      result = result.filter(teacher => {
        if (filters.lessonFormat === 'both') {
          return teacher.lesson_format_preference === 'both';
        }
        return teacher.lesson_format_preference === filters.lessonFormat ||
               teacher.lesson_format_preference === 'both';
      });
    }

    // Sorting
    if (sortBy === 'price_low') {
      result.sort((a, b) => (a.hourly_rate || 0) - (b.hourly_rate || 0));
    } else if (sortBy === 'price_high') {
      result.sort((a, b) => (b.hourly_rate || 0) - (a.hourly_rate || 0));
    } else if (sortBy === 'rating') {
      result.sort((a, b) => {
        const ratingA = teacherRatings[a.email]?.avg_rating || 0;
        const ratingB = teacherRatings[b.email]?.avg_rating || 0;
        return ratingB - ratingA;
      });
    }

    return result;
  }, [teachers, search, filters, teacherRatings, sortBy]);

  // Handle favorite toggle
  const handleToggleFavorite = async (e, teacherId) => {
    e.stopPropagation();
    if (!currentUser) return;

    const isFavorite = favoriteTeacherIds.includes(teacherId);
    await toggleFavoriteTeacher(currentUser.uuid_id, teacherId);

    if (isFavorite) {
      setFavoriteTeacherIds(prev => prev.filter(id => id !== teacherId));
    } else {
      setFavoriteTeacherIds(prev => [...prev, teacherId]);
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setPage(1);
  };

  // Toggle multi-select filters
  const toggleMultiSelectFilter = (filterType, value) => {
    setFilters(prev => {
      const currentValues = prev[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterType]: newValues };
    });
    setPage(1);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      subjects: [],
      grades: [],
      price: [0, 30],
      rating: 'all',
      languages: [],
      gender: null,
      lessonFormat: null,
      favoritesOnly: false
    });
    setSearch('');
    setSortBy('recommended');
    setPage(1);
  };

  // Calculate total pages
  const totalPages = Math.ceil(filteredTeachers.length / teachersPerPage);
  const displayedTeachers = filteredTeachers.slice(
    (page - 1) * teachersPerPage,
    page * teachersPerPage
  );

  // Filter Dropdown Component
  const FilterDropdown = ({ id, label, icon: Icon, children, displayValue }) => {
    const isOpen = openFilterDropdown === id;
    
    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setOpenFilterDropdown(isOpen ? null : id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.borderLight}`,
            borderRadius: '24px',
            cursor: 'pointer',
            fontSize: '14px',
            color: COLORS.dark,
            fontWeight: 500,
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
        >
          {Icon && <Icon style={{ color: COLORS.textSecondary, fontSize: '14px' }} />}
          <span>{displayValue || label}</span>
          <FaChevronDown style={{ 
            fontSize: '10px', 
            color: COLORS.textSecondary,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }} />
        </button>
        
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            backgroundColor: COLORS.white,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            padding: '16px',
            minWidth: '240px',
            zIndex: 1001,
            maxHeight: '320px',
            overflowY: 'auto'
          }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  // Two-Row Filter Section - Using useMemo to prevent re-creation on every render
  const filterSectionJSX = useMemo(() => (
      <div style={{
        marginBottom: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {/* All Filters in One Row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center'
        }}>
          {/* Subject Filter */}
          <FilterDropdown
            id="subjects"
            label={isRTL ? 'المادة' : 'Subject'}
            icon={FaBook}
            displayValue={filters.subjects.length > 0 
              ? (filters.subjects.length === 1 ? filters.subjects[0] : `${filters.subjects.length} selected`)
              : (isRTL ? 'المادة' : 'Subject')
            }
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {subjectOptions.map(subject => (
                <button
                  key={subject}
                  onClick={() => toggleMultiSelectFilter('subjects', subject)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    backgroundColor: filters.subjects.includes(subject) ? COLORS.orange : '#F3F4F6',
                    color: filters.subjects.includes(subject) ? 'white' : COLORS.dark,
                    transition: 'all 0.2s'
                  }}
                >
                  {translateSubject(subject, isRTL)}
                </button>
              ))}
            </div>
          </FilterDropdown>

          {/* Price Filter */}
          <FilterDropdown
            id="price"
            label={isRTL ? 'السعر' : 'Price per lesson'}
            icon={FaDollarSign}
            displayValue={filters.price[0] === 0 && filters.price[1] === 30 
              ? (isRTL ? 'السعر' : 'Price per lesson')
              : isRTL
                ? `${toArabicNumerals(filters.price[0], isRTL)} – ${toArabicNumerals(filters.price[1], isRTL)} د.ب`
                : `${filters.price[0]} – ${filters.price[1]} BD`
            }
          >
            <div>
              <div style={{ marginBottom: '16px', fontSize: '14px', fontWeight: 600, color: COLORS.dark }}>
                {isRTL ? 'نطاق السعر (BD/ساعة)' : 'Price Range (BD/hr)'}
              </div>
              <input
                type="range"
                min="0"
                max="30"
                step="1"
                value={filters.price[1]}
                onChange={(e) => handleFilterChange('price', [0, parseInt(e.target.value)])}
                style={{ 
                  width: '100%', 
                  accentColor: COLORS.orange,
                  transition: 'all 0.2s ease',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '13px', color: COLORS.textSecondary }}>
                <span>{isRTL ? `${toArabicNumerals(0, isRTL)} د.ب` : '0 BD'}</span>
                <span style={{ fontWeight: 600, color: COLORS.orange }}>
                  {toArabicNumerals(filters.price[1], isRTL)} {isRTL ? 'د.ب+' : 'BD+'}
                </span>
              </div>
            </div>
          </FilterDropdown>

          {/* Grade Level Filter */}
          <FilterDropdown
            id="grades"
            label={isRTL ? 'المستوى' : 'Grade Level'}
            icon={FaGraduationCap}
            displayValue={filters.grades.length > 0 
              ? (filters.grades.length === 1 
                  ? translateGrade(filters.grades[0], isRTL)
                  : isRTL 
                    ? `${toArabicNumerals(filters.grades.length, isRTL)} محدد`
                    : `${filters.grades.length} selected`)
              : (isRTL ? 'المستوى' : 'Grade Level')
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {gradeOptions.map(grade => (
                <button
                  key={grade}
                  onClick={() => toggleMultiSelectFilter('grades', grade)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    backgroundColor: filters.grades.includes(grade) ? COLORS.orange : '#F3F4F6',
                    color: filters.grades.includes(grade) ? 'white' : COLORS.dark,
                    textAlign: isRTL ? 'right' : 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  {translateGrade(grade, isRTL)}
                </button>
              ))}
            </div>
          </FilterDropdown>

          {/* Rating Filter */}
          <FilterDropdown
            id="rating"
            label={isRTL ? 'التقييم' : 'Rating'}
            icon={FaStar}
            displayValue={filters.rating === 'all' 
              ? (isRTL ? 'التقييم' : 'Rating')
              : `${filters.rating}★+`
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {['all', '5', '4.5', '4', '3.5'].map(rating => (
                <button
                  key={rating}
                  onClick={() => {
                    handleFilterChange('rating', rating);
                    setOpenFilterDropdown(null);
                  }}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: filters.rating === rating ? 600 : 400,
                    cursor: 'pointer',
                    backgroundColor: filters.rating === rating ? COLORS.orangeLight : 'transparent',
                    color: filters.rating === rating ? COLORS.orange : COLORS.dark,
                    textAlign: isRTL ? 'right' : 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  {rating === 'all' ? (isRTL ? 'جميع التقييمات' : 'All Ratings') : `${rating} ★ ${isRTL ? 'وأعلى' : '& up'}`}
                </button>
              ))}
            </div>
          </FilterDropdown>

          {/* Language Filter */}
          <FilterDropdown
            id="languages"
            label={isRTL ? 'اللغة' : 'Also speaks'}
            icon={FaGlobe}
            displayValue={filters.languages.length > 0 
              ? (filters.languages.length === 1 
                  ? translateLanguage(filters.languages[0], isRTL)
                  : isRTL 
                    ? `${toArabicNumerals(filters.languages.length, isRTL)} لغات`
                    : `${filters.languages.length} languages`)
              : (isRTL ? 'اللغة' : 'Also speaks')
            }
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {languageOptions.map(lang => (
                <button
                  key={lang}
                  onClick={() => toggleMultiSelectFilter('languages', lang)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    backgroundColor: filters.languages.includes(lang) ? COLORS.orange : '#F3F4F6',
                    color: filters.languages.includes(lang) ? 'white' : COLORS.dark,
                    transition: 'all 0.2s'
                  }}
                >
                  {lang}
                </button>
              ))}
            </div>
          </FilterDropdown>

          {/* Gender Filter */}
          <FilterDropdown
            id="gender"
            label={isRTL ? 'الجنس' : 'Gender'}
            icon={FaUser}
            displayValue={filters.gender 
              ? (filters.gender === 'male' ? (isRTL ? 'ذكر' : 'Male') : (isRTL ? 'أنثى' : 'Female'))
              : (isRTL ? 'الجنس' : 'Gender')
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <button
                onClick={() => { handleFilterChange('gender', null); setOpenFilterDropdown(null); }}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: !filters.gender ? 600 : 400,
                  cursor: 'pointer',
                  backgroundColor: !filters.gender ? COLORS.orangeLight : 'transparent',
                  color: !filters.gender ? COLORS.orange : COLORS.dark,
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {isRTL ? 'الكل' : 'All'}
              </button>
              <button
                onClick={() => { handleFilterChange('gender', 'male'); setOpenFilterDropdown(null); }}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: filters.gender === 'male' ? 600 : 400,
                  cursor: 'pointer',
                  backgroundColor: filters.gender === 'male' ? COLORS.orangeLight : 'transparent',
                  color: filters.gender === 'male' ? COLORS.orange : COLORS.dark,
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {isRTL ? 'ذكر' : 'Male'}
              </button>
              <button
                onClick={() => { handleFilterChange('gender', 'female'); setOpenFilterDropdown(null); }}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: filters.gender === 'female' ? 600 : 400,
                  cursor: 'pointer',
                  backgroundColor: filters.gender === 'female' ? COLORS.orangeLight : 'transparent',
                  color: filters.gender === 'female' ? COLORS.orange : COLORS.dark,
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {isRTL ? 'أنثى' : 'Female'}
              </button>
            </div>
          </FilterDropdown>

          {/* Lesson Format Filter */}
          <FilterDropdown
            id="lessonFormat"
            label={isRTL ? 'نوع الدرس' : 'Lesson type'}
            icon={FaUsers}
            displayValue={filters.lessonFormat 
              ? (filters.lessonFormat === 'individual' ? (isRTL ? 'فردي' : 'Individual') : 
                 filters.lessonFormat === 'group' ? (isRTL ? 'جماعي' : 'Group') : (isRTL ? 'كلاهما' : 'Both'))
              : (isRTL ? 'نوع الدرس' : 'Lesson type')
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {[null, 'individual', 'group', 'both'].map(format => (
                <button
                  key={format || 'all'}
                  onClick={() => { handleFilterChange('lessonFormat', format); setOpenFilterDropdown(null); }}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: filters.lessonFormat === format ? 600 : 400,
                    cursor: 'pointer',
                    backgroundColor: filters.lessonFormat === format ? COLORS.orangeLight : 'transparent',
                    color: filters.lessonFormat === format ? COLORS.orange : COLORS.dark,
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                >
                  {format === null && (isRTL ? 'الكل' : 'All')}
                  {format === 'individual' && (isRTL ? 'فردي' : 'Individual')}
                  {format === 'group' && (isRTL ? 'جماعي' : 'Group')}
                  {format === 'both' && (isRTL ? 'كلاهما' : 'Both')}
                </button>
              ))}
            </div>
          </FilterDropdown>
        </div>

        {/* Sort and Search Row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'flex-end'
        }}>
          {/* Right side: Sort and Search */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Sort Dropdown */}
            <FilterDropdown
              id="sort"
              label={isRTL ? 'ترتيب' : 'Sort by'}
              displayValue={sortOptions.find(o => o.value === sortBy)?.[isRTL ? 'labelAr' : 'label'] || (isRTL ? 'اختياراتنا' : 'Our top picks')}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {sortOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => { setSortBy(option.value); setOpenFilterDropdown(null); }}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: 'none',
                      fontSize: '13px',
                      fontWeight: sortBy === option.value ? 600 : 400,
                      cursor: 'pointer',
                      backgroundColor: sortBy === option.value ? COLORS.orangeLight : 'transparent',
                      color: sortBy === option.value ? COLORS.orange : COLORS.dark,
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  >
                    {isRTL ? option.labelAr : option.label}
                  </button>
                ))}
              </div>
            </FilterDropdown>

            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: isMobile ? '100%' : '200px' }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isRTL ? 'البحث بالاسم...' : 'Search by name or keyword'}
                style={{
                  width: '100%',
                  padding: isRTL ? '10px 36px 10px 14px' : '10px 14px 10px 36px',
                  borderRadius: '24px',
                  border: `1px solid ${COLORS.borderLight}`,
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: COLORS.white
                }}
              />
              <FaSearch style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: COLORS.textSecondary,
                fontSize: '14px'
              }} />
            </div>
          </div>
        </div>
      </div>
  ), [filters, search, sortBy, isRTL, isMobile, openFilterDropdown]);

  // Horizontal Teacher Card Component - Responsive for all devices
  const TeacherCard = ({ teacher, isSelected, onSelect }) => {
    const rating = teacherRatings[teacher.email];
    const displayPrice = teacher.lesson_format_preference === 'group'
      ? teacher.group_hourly_rate
      : teacher.hourly_rate;
    const subjects = teacher.subjects || [];
    const languages = teacher.languages || teacher.teaching_language || [];
    const isFavorite = favoriteTeacherIds.includes(teacher.uuid_id);

    // Use actual data from teacher record
    const reviewCount = rating?.ratings_count || 0;
    const studentCount = teacher.total_students || 0;
    const lessonCount = teacher.total_lessons || 0;

    // Mobile layout - stacked
    if (isMobile) {
      return (
        <div
          onClick={() => onSelect(teacher)}
          style={{
            backgroundColor: COLORS.white,
            borderRadius: '12px',
            border: isSelected ? `2px solid ${COLORS.orange}` : `1px solid ${COLORS.borderLight}`,
            padding: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: isSelected ? '0 4px 12px rgba(255, 122, 26, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
            marginBottom: '12px'
          }}
        >
          {/* Top row: Photo, Name, Price */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <img
              src={teacher.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
              alt={teacher.full_name}
              style={{
                width: '110px',
                height: '110px',
                borderRadius: '8px',
                objectFit: 'cover',
                objectPosition: 'center',
                backgroundColor: '#F3F4F6',
                flexShrink: 0
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: COLORS.dark,
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}                >
                  {isRTL ? transliterateNameSync(teacher.full_name, isRTL) : capitalizeName(teacher.full_name)}
                </h3>
              </div>
              <div style={{ fontSize: '16px', color: COLORS.textSecondary, marginBottom: '4px' }}>
                {subjects.slice(0, 2).map(s => translateSubject(s, isRTL)).join(', ')}
                {subjects.length > 2 && ` +${toArabicNumerals(subjects.length - 2, isRTL)}`}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700, color: COLORS.dark }}>
                  {toArabicNumerals(displayPrice || 15, isRTL)} {isRTL ? 'د.ب' : 'BD'}
                </span>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>{isRTL ? '/ساعة' : '/hr'}</span>
                {rating && rating.avg_rating > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: 'auto' }}>
                    <FaStar style={{ color: '#F59E0B', fontSize: '12px' }} />
                    <span style={{ fontWeight: 600, fontSize: '13px', color: COLORS.dark }}>
                      {toArabicNumerals(rating.avg_rating.toFixed(1), isRTL)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={(e) => handleToggleFavorite(e, teacher.uuid_id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                fontSize: '18px',
                color: isFavorite ? '#EF4444' : COLORS.textSecondary,
                alignSelf: 'flex-start'
              }}
            >
              {isFavorite ? <FaHeart /> : <FaRegHeart />}
            </button>
          </div>

          {/* Bio */}
          <p style={{
            fontSize: '15px',
            color: COLORS.textSecondary,
            margin: '0 0 12px 0',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {teacher.bio?.substring(0, 100) || (isRTL ? 'مرحباً! أنا معلم شغوف بالتدريس...' : 'Hi! I\'m a passionate educator...')}
          </p>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teacher/${teacher.uuid_id}`);
              }}
              style={{
                flex: 1,
                padding: '10px 12px',
                backgroundColor: COLORS.orange,
                color: COLORS.white,
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {isRTL ? 'طلب جلسة' : 'Request'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setProfilePopupTeacher(teacher);
              }}
              style={{
                flex: 1,
                padding: '10px 12px',
                backgroundColor: COLORS.white,
                color: COLORS.dark,
                border: `1px solid ${COLORS.borderLight}`,
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {isRTL ? 'عرض الملف' : 'View profile'}
            </button>
          </div>
        </div>
      );
    }

    // Tablet layout - compact horizontal
    if (isTablet) {
      return (
        <div
          onClick={() => onSelect(teacher)}
          style={{
            backgroundColor: COLORS.white,
            borderRadius: '12px',
            border: isSelected ? `2px solid ${COLORS.orange}` : `1px solid ${COLORS.borderLight}`,
            padding: '16px',
            display: 'flex',
            gap: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: isSelected ? '0 4px 12px rgba(255, 122, 26, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
            marginBottom: '12px'
          }}
        >
          {/* Photo */}
          <img
            src={teacher.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
            alt={teacher.full_name}
            style={{
              width: '140px',
              height: '150px',
              borderRadius: '8px',
              objectFit: 'cover',
              objectPosition: 'center',
              backgroundColor: '#F3F4F6',
              flexShrink: 0
            }}
          />

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 700,
                color: COLORS.dark,
                margin: 0
              }}              >
                {isRTL ? transliterateNameSync(teacher.full_name, isRTL) : capitalizeName(teacher.full_name)}
              </h3>
            </div>

            <div style={{ fontSize: '16px', color: COLORS.textSecondary, marginBottom: '6px' }}>
              {subjects.slice(0, 2).map(s => translateSubject(s, isRTL)).join(', ')}
            </div>

            <p style={{
              fontSize: '15px',
              color: COLORS.textSecondary,
              margin: '0 0 8px 0',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {teacher.bio?.substring(0, 100) || (isRTL ? 'معلم متخصص ومتمرس' : 'Experienced & dedicated tutor')}
            </p>

          </div>

          {/* Price and Actions */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            minWidth: '130px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.dark }}>
                  {toArabicNumerals(displayPrice || 15, isRTL)} {isRTL ? 'د.ب' : 'BD'}
                </div>
                <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{isRTL ? '/ساعة' : '/hr'}</div>
              </div>
              <button
                onClick={(e) => handleToggleFavorite(e, teacher.uuid_id)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  fontSize: '18px',
                  color: isFavorite ? '#EF4444' : COLORS.textSecondary
                }}
              >
                {isFavorite ? <FaHeart /> : <FaRegHeart />}
              </button>
            </div>

            {rating && rating.avg_rating > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FaStar style={{ color: '#F59E0B', fontSize: '13px' }} />
                <span style={{ fontWeight: 600, fontSize: '13px', color: COLORS.dark }}>
                  {toArabicNumerals(rating.avg_rating.toFixed(1), isRTL)}
                </span>
                <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                  ({toArabicNumerals(reviewCount, isRTL)})
                </span>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/teacher/${teacher.uuid_id}`);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  backgroundColor: COLORS.orange,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {isRTL ? 'طلب جلسة' : 'Request'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setProfilePopupTeacher(teacher);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  backgroundColor: COLORS.white,
                  color: COLORS.dark,
                  border: `1px solid ${COLORS.borderLight}`,
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {isRTL ? 'عرض الملف' : 'View profile'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Desktop layout - full horizontal
    return (
      <div
        onClick={() => onSelect(teacher)}
        style={{
          backgroundColor: COLORS.white,
          borderRadius: '12px',
          border: isSelected ? `2px solid ${COLORS.orange}` : `1px solid ${COLORS.borderLight}`,
          padding: '20px',
          display: 'flex',
          gap: '20px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: isSelected ? '0 4px 12px rgba(255, 122, 26, 0.2)' : '0 2px 8px rgba(0,0,0,0.04)'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
          }
        }}
      >
        {/* Left: Profile Photo */}
        <div style={{ flexShrink: 0 }}>
          <img
            src={teacher.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
            alt={teacher.full_name}
            style={{
              width: '180px',
              height: '200px',
              borderRadius: '8px',
              objectFit: 'cover',
              objectPosition: 'center',
              backgroundColor: '#F3F4F6'
            }}
          />
        </div>

        {/* Middle: Teacher Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <h3 style={{
              fontSize: '22px',
              fontWeight: 700,
              color: COLORS.dark,
              margin: 0
            }}>
              {isRTL ? transliterateNameSync(teacher.full_name, isRTL) : capitalizeName(teacher.full_name)}
            </h3>
            {teacher.country && (
              <span style={{ fontSize: '18px' }}>🇧🇭</span>
            )}
          </div>

          {/* Professional Badge */}
          {teacher.is_professional && (
            <span style={{
              display: 'inline-block',
              padding: '4px 10px',
              backgroundColor: '#EEF2FF',
              color: '#4F46E5',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '8px'
            }}>
              {isRTL ? 'محترف' : 'Professional'}
            </span>
          )}

          {/* Subject */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <FaBook style={{ color: COLORS.textSecondary, fontSize: '16px' }} />
            <span style={{ fontSize: '16px', color: COLORS.textSecondary }}>
              {subjects.slice(0, 2).map(s => translateSubject(s, isRTL)).join(', ')}
              {subjects.length > 2 && ` +${subjects.length - 2}`}
            </span>
          </div>

          {/* Languages */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <FaGlobe style={{ color: COLORS.textSecondary, fontSize: '16px' }} />
            <span style={{ fontSize: '16px', color: COLORS.textSecondary }}>
              {isRTL ? 'يتحدث' : 'Speaks'} {languages.slice(0, 2).map(l => {
                // Handle both string format and object format
                const languageName = typeof l === 'string' ? l : (l.language || '');
                return translateLanguage(languageName, isRTL);
              }).join(', ')}
              {languages.length > 2 && ` +${languages.length - 2}`}
            </span>
          </div>

          {/* Bio/Headline */}
          <p style={{
            fontSize: '16px',
            fontWeight: 600,
            color: COLORS.dark,
            margin: '0 0 4px 0',
            lineHeight: 1.4
          }}>
            {teacher.headline || (isRTL ? 'معلم متخصص ومتمرس' : 'Experienced & Dedicated Tutor')}
          </p>

          {/* Bio snippet */}
          <p style={{
            fontSize: '15px',
            color: COLORS.textSecondary,
            margin: '0 0 8px 0',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {teacher.bio?.substring(0, 120) || (isRTL ? 'مرحباً! أنا معلم شغوف بالتدريس...' : 'Hi! I\'m a passionate educator dedicated to helping students...')}
            {teacher.bio?.length > 120 && '...'}
          </p>

          {/* Popular badge */}
          {reviewCount > 5 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaBolt style={{ color: '#F59E0B', fontSize: '14px' }} />
              <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
                {isRTL ? 'شائع جداً' : 'Super popular'}.{' '}
                {isRTL 
                  ? `تم حجزه ${toArabicNumerals(reviewCount, isRTL)}+ مرة مؤخراً` 
                  : `Booked ${toArabicNumerals(reviewCount, isRTL)}+ times recently`}
              </span>
            </div>
          )}
        </div>

        {/* Right: Price and Actions */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          minWidth: '160px'
        }}>
          {/* Price */}
          <div style={{ textAlign: 'right', marginBottom: '12px' }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 700,
              color: COLORS.dark
            }}>
              {toArabicNumerals(displayPrice || 15, isRTL)} {isRTL ? 'د.ب' : 'BD'}
            </div>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
              {isRTL ? 'جلسة ساعة واحدة' : '1 hour session'}
            </div>
            <button
              onClick={(e) => handleToggleFavorite(e, teacher.uuid_id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                fontSize: '20px',
                color: isFavorite ? '#EF4444' : COLORS.textSecondary
              }}
            >
              {isFavorite ? <FaHeart /> : <FaRegHeart />}
            </button>
          </div>

          {/* Stats Row - Only show actual data */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}>
            {rating && rating.avg_rating > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FaStar style={{ color: '#F59E0B', fontSize: '14px' }} />
                <span style={{ fontWeight: 600, color: COLORS.dark }}>
                  {toArabicNumerals(rating.avg_rating.toFixed(1), isRTL)}
                </span>
              </div>
            )}
            {reviewCount > 0 && (
              <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
                {toArabicNumerals(reviewCount, isRTL)} {isRTL ? 'تقييم' : 'reviews'}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teacher/${teacher.uuid_id}`);
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: COLORS.orange,
                color: COLORS.white,
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(255, 122, 26, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = COLORS.orangeHover;
                e.target.style.boxShadow = '0 4px 12px rgba(255, 122, 26, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = COLORS.orange;
                e.target.style.boxShadow = '0 2px 8px rgba(255, 122, 26, 0.3)';
              }}
            >
              {isRTL ? 'طلب جلسة' : 'Request session'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setProfilePopupTeacher(teacher);
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: COLORS.white,
                color: COLORS.dark,
                border: `1px solid ${COLORS.borderLight}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#F9FAFB'}
              onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.white}
            >
              {isRTL ? 'عرض الملف' : 'View profile'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Teacher Sidebar Component - Shows video matching card height
  const TeacherSidebar = ({ teacher }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef(null);

    // Don't render anything if no teacher selected or no video
    if (!teacher || !teacher.intro_video_url) {
      return null;
    }

    const handlePlayVideo = () => {
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    };

    return (
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '12px',
        border: `1px solid ${COLORS.borderLight}`,
        overflow: 'hidden',
        height: '190px'
      }}>
        {/* Video Player - Fixed height to match card */}
        <div style={{
          position: 'relative',
          backgroundColor: '#000',
          height: '100%'
        }}>
          <video
            ref={videoRef}
            src={teacher.intro_video_url}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            poster={teacher.profile_photo}
            onEnded={() => setIsPlaying(false)}
            controls={isPlaying}
          />
          {/* Play Button Overlay - only show when not playing */}
          {!isPlaying && (
            <div 
              onClick={handlePlayVideo}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '60px',
                height: '60px',
                backgroundColor: COLORS.orange,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(255, 122, 26, 0.5)',
                transition: 'all 0.2s'
              }}
            >
              <FaPlay style={{ color: COLORS.white, fontSize: '20px', marginLeft: '3px' }} />
            </div>
          )}
        </div>
      </div>
    );
  };

  // Pagination Component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '32px'
      }}>
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.borderLight}`,
            backgroundColor: COLORS.white,
            cursor: page === 1 ? 'not-allowed' : 'pointer',
            opacity: page === 1 ? 0.5 : 1
          }}
        >
          <FaChevronLeft />
        </button>

        {pages.map(p => (
          <button
            key={p}
            onClick={() => setPage(p)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: p === page ? 'none' : `1px solid ${COLORS.borderLight}`,
              backgroundColor: p === page ? COLORS.orange : COLORS.white,
              color: p === page ? COLORS.white : COLORS.dark,
              fontWeight: p === page ? 600 : 400,
              cursor: 'pointer'
            }}
          >
            {p}
          </button>
        ))}

        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.borderLight}`,
            backgroundColor: COLORS.white,
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
            opacity: page === totalPages ? 0.5 : 1
          }}
        >
          <FaChevronRight />
        </button>
      </div>
    );
  };

  // Mobile Filter Modal
  const MobileFilterModal = () => {
    if (!showMobileFilters) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end'
      }}>
        <div style={{
          backgroundColor: COLORS.white,
          width: '100%',
          maxHeight: '90vh',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          overflow: 'hidden',
          animation: 'slideUp 0.3s ease-out',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px',
            borderBottom: `1px solid ${COLORS.borderLight}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>
              {isRTL ? 'الفلاتر' : 'Filters'}
            </h3>
            <button
              onClick={() => setShowMobileFilters(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: COLORS.textSecondary
              }}
            >
              <FaTimes />
            </button>
          </div>

          {/* Filter Content */}
          <div style={{ 
            padding: '20px', 
            overflowY: 'auto', 
            flex: 1,
            minHeight: 0,
            paddingBottom: '10px'
          }}>
            {/* Subject */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>
                {isRTL ? 'المادة' : 'Subject'}
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {subjectOptions.map(subject => (
                  <button
                    key={subject}
                    onClick={() => toggleMultiSelectFilter('subjects', subject)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '20px',
                      border: 'none',
                      fontSize: '13px',
                      backgroundColor: filters.subjects.includes(subject) ? COLORS.orange : '#F3F4F6',
                      color: filters.subjects.includes(subject) ? 'white' : COLORS.dark
                    }}
                  >
                    {translateSubject(subject, isRTL)}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>
                {isRTL ? 'السعر' : 'Price Range'}
              </h4>
              <input
                type="range"
                min="0"
                max="30"
                step="1"
                value={filters.price[1]}
                onChange={(e) => handleFilterChange('price', [0, parseInt(e.target.value)])}
                style={{ 
                  width: '100%', 
                  accentColor: COLORS.orange,
                  transition: 'all 0.2s ease',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                <span>0 BD</span>
                <span style={{ fontWeight: 600, color: COLORS.orange }}>{filters.price[1]} BD</span>
              </div>
            </div>

            {/* Grade Level */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>
                {isRTL ? 'المستوى' : 'Grade Level'}
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {gradeOptions.map(grade => (
                  <button
                    key={grade}
                    onClick={() => toggleMultiSelectFilter('grades', grade)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '20px',
                      border: 'none',
                      fontSize: '13px',
                      backgroundColor: filters.grades.includes(grade) ? COLORS.orange : '#F3F4F6',
                      color: filters.grades.includes(grade) ? 'white' : COLORS.dark
                    }}
                  >
                    {translateGrade(grade, isRTL)}
                  </button>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>
                {isRTL ? 'اللغة' : 'Language'}
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {languageOptions.map(lang => (
                  <button
                    key={lang}
                    onClick={() => toggleMultiSelectFilter('languages', lang)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '20px',
                      border: 'none',
                      fontSize: '13px',
                      backgroundColor: filters.languages.includes(lang) ? COLORS.orange : '#F3F4F6',
                      color: filters.languages.includes(lang) ? 'white' : COLORS.dark
                    }}
                  >
                    {translateLanguage(lang, isRTL)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Apply Button */}
          <div style={{ 
            padding: '16px 20px 20px 20px',
            paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
            borderTop: `1px solid ${COLORS.borderLight}`,
            flexShrink: 0,
            backgroundColor: COLORS.white
          }}>
            <button
              onClick={() => setShowMobileFilters(false)}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: COLORS.orange,
                color: COLORS.white,
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {isRTL ? 'تطبيق الفلاتر' : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      style={{
        backgroundColor: '#F8FAFC',
        minHeight: '100vh',
        padding: isMobile ? '16px' : '24px'
      }}
      className={isRTL ? 'rtl' : 'ltr'} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Click outside handler to close dropdowns */}
      {openFilterDropdown && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000
          }}
          onClick={() => setOpenFilterDropdown(null)}
        />
      )}

      {/* Main Container */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{
            fontSize: isMobile ? '24px' : '32px',
            fontWeight: 700,
            color: COLORS.dark,
            margin: '0 0 8px 0'
          }}>
            {isRTL ? 'ابحث عن معلم' : 'Find a Tutor'}
          </h1>
          <p style={{
            fontSize: '15px',
            color: COLORS.textSecondary,
            margin: 0
          }}>
            {isRTL 
              ? `${toArabicNumerals(totalTeachers, isRTL)} معلم متاح للتدريس`
              : `${totalTeachers} tutors available to help you learn`
            }
          </p>
        </div>

        {/* Filter Section - Desktop */}
        {!isMobile && filterSectionJSX}

        {/* Mobile Filter Button */}
        {isMobile && (
          <button
            onClick={() => setShowMobileFilters(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.borderLight}`,
              borderRadius: '24px',
              cursor: 'pointer',
              marginBottom: '16px',
              width: '100%',
              justifyContent: 'center'
            }}
          >
            <FaFilter style={{ color: COLORS.textSecondary }} />
            <span style={{ fontWeight: 500 }}>{isRTL ? 'الفلاتر' : 'Filters'}</span>
          </button>
        )}

        {/* Main Content Area */}
        <div style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'flex-start'
        }}>
          {/* Teacher List */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {loadingTeachers ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 0'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: `4px solid ${COLORS.borderLight}`,
                  borderTopColor: COLORS.orange,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              </div>
            ) : displayedTeachers.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: COLORS.white,
                borderRadius: '12px',
                border: `1px solid ${COLORS.borderLight}`
              }}>
                <FaSearch style={{ fontSize: '48px', color: COLORS.textSecondary, marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ margin: '0 0 8px 0', color: COLORS.dark }}>
                  {isRTL ? 'لا توجد نتائج' : 'No Results Found'}
                </h3>
                <p style={{ color: COLORS.textSecondary, margin: '0 0 20px 0' }}>
                  {isRTL ? 'جرب تغيير الفلاتر' : 'Try adjusting your filters'}
                </p>
                <button
                  onClick={clearAllFilters}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: COLORS.orange,
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
                </button>
              </div>
            ) : (
              <>
                {/* Results count */}
                <div style={{
                  marginBottom: '16px',
                  fontSize: '15px',
                  color: COLORS.textSecondary
                }}>
                  {isRTL 
                    ? `عرض ${toArabicNumerals(displayedTeachers.length, isRTL)} من ${toArabicNumerals(filteredTeachers.length, isRTL)} معلم`
                    : `Showing ${displayedTeachers.length} of ${filteredTeachers.length} tutors`
                  }
                </div>

                {/* Teacher Cards with inline video on hover */}
                {displayedTeachers.map((teacher, index) => {
                  const isHovered = selectedTeacher?.uuid_id === teacher.uuid_id;
                  const hasVideo = teacher.intro_video_url;
                  const showVideoInline = !isMobile && !isTablet && isHovered && hasVideo;
                  
                  return (
                    <div 
                      key={teacher.uuid_id}
                      style={{
                        display: showVideoInline ? 'flex' : 'block',
                        gap: '20px',
                        alignItems: 'center',
                        marginBottom: '16px',
                        animation: `cardFadeIn 0.4s ease-out ${index * 0.08}s forwards`,
                        opacity: 0
                      }}
                      onMouseEnter={() => {
                        if (!isMobile && !isTablet && hasVideo) {
                          setSelectedTeacher(teacher);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isMobile && !isTablet) {
                          setSelectedTeacher(null);
                        }
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <TeacherCard
                          teacher={teacher}
                          isSelected={isHovered}
                          onSelect={() => {}} // Click no longer needed for video
                        />
                      </div>
                      
                      {/* Video Box - Shows inline beside hovered teacher card */}
                      {showVideoInline && (
                        <div style={{ width: '320px', flexShrink: 0 }}>
                          <TeacherSidebar teacher={teacher} />
                        </div>
                      )}
                    </div>
                  );
                })}

                <Pagination />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      <MobileFilterModal />

      {/* Teacher Profile Popup Modal - Premium Design */}
      {profilePopupTeacher && (() => {
        const popupRating = teacherRatings[profilePopupTeacher.email];
        const avgRating = popupRating?.avg_rating || 0;
        const reviewCount = popupRating?.ratings_count || 0;
        const isFavorite = favoriteTeacherIds.includes(profilePopupTeacher.uuid_id);

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '12px' : '24px',
              animation: 'popupOverlayFade 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onClick={() => setProfilePopupTeacher(null)}
          >
            <div
              style={{
                backgroundColor: COLORS.white,
                borderRadius: '24px',
                maxWidth: isMobile ? '100%' : '480px',
                width: '100%',
                maxHeight: isMobile ? '95vh' : '90vh',
                overflow: 'hidden',
                boxShadow: '0 25px 80px rgba(0, 0, 0, 0.35), 0 10px 30px rgba(255, 122, 26, 0.15)',
                animation: 'popupContentSlide 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient background */}
              <div style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #FF7A1A 0%, #FF9A4D 50%, #FFB366 100%)',
                padding: '20px 20px 70px 20px',
                minHeight: '140px'
              }}>
                {/* Close Button */}
                <button
                  onClick={() => setProfilePopupTeacher(null)}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COLORS.white,
                    fontSize: '16px',
                    zIndex: 10,
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(4px)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.35)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <FaTimes />
                </button>

                {/* Favorite Button */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (currentUser) {
                      await toggleFavoriteTeacher(currentUser.uuid_id, profilePopupTeacher.uuid_id);
                      setFavoriteTeacherIds(prev =>
                        prev.includes(profilePopupTeacher.uuid_id)
                          ? prev.filter(id => id !== profilePopupTeacher.uuid_id)
                          : [...prev, profilePopupTeacher.uuid_id]
                      );
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isFavorite ? '#EF4444' : COLORS.white,
                    fontSize: '16px',
                    zIndex: 10,
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(4px)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.35)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  {isFavorite ? <FaHeart /> : <FaRegHeart />}
                </button>

                {/* Decorative circles */}
                <div style={{
                  position: 'absolute',
                  top: '-30px',
                  right: '-30px',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: '40px',
                  left: '-20px',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }} />
              </div>

              {/* Profile Photo - Overlapping header */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: '-60px',
                position: 'relative',
                zIndex: 5
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '4px solid white',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(255, 122, 26, 0.2)',
                  backgroundColor: COLORS.white,
                  animation: 'popupPhotoScale 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both'
                }}>
                  <img
                    src={profilePopupTeacher.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'}
                    alt={profilePopupTeacher.full_name}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                </div>
              </div>

              {/* Scrollable Content */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px 24px 24px',
                scrollbarWidth: 'thin',
                scrollbarColor: `${COLORS.orange} ${COLORS.gray}`
              }}>
                {/* Name */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px',
                  animation: 'popupItemFade 0.4s ease 0.15s both'
                }}>
                  <h2 style={{
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: 700,
                    color: COLORS.dark,
                    textAlign: 'center'
                  }}>
                    {isRTL ? transliterateNameSync(profilePopupTeacher.full_name, isRTL) : profilePopupTeacher.full_name}
                  </h2>
                </div>

                {/* Headline / Professional Title */}
                {profilePopupTeacher.headline && (
                  <p style={{
                    margin: '0 0 12px 0',
                    fontSize: '14px',
                    color: COLORS.textSecondary,
                    textAlign: 'center',
                    fontStyle: 'italic',
                    animation: 'popupItemFade 0.4s ease 0.2s both'
                  }}>
                    {profilePopupTeacher.headline}
                  </p>
                )}

                {/* Stats Row - Price, Rating, Experience */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '20px',
                  marginBottom: '20px',
                  padding: '16px',
                  backgroundColor: COLORS.gray,
                  borderRadius: '16px',
                  animation: 'popupItemFade 0.4s ease 0.25s both'
                }}>
                  {/* Price */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 700,
                      color: COLORS.orange,
                      lineHeight: 1
                    }}>
                      {toArabicNumerals(profilePopupTeacher.hourly_rate || 15, isRTL)} <span style={{ fontSize: '14px', fontWeight: 500 }}>{isRTL ? 'د.ب' : 'BD'}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '2px' }}>
                      {isRTL ? 'في الساعة' : 'per hour'}
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{ width: '1px', height: '40px', backgroundColor: COLORS.borderLight }} />

                  {/* Rating */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}>
                      <FaStar style={{ color: '#FBBF24', fontSize: '18px' }} />
                      <span style={{ fontSize: '20px', fontWeight: 700, color: COLORS.dark }}>
                        {avgRating > 0 ? toArabicNumerals(avgRating.toFixed(1), isRTL) : '-'}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '2px' }}>
                      {reviewCount > 0 ? `${toArabicNumerals(reviewCount, isRTL)} ${isRTL ? 'تقييم' : 'reviews'}` : (isRTL ? 'جديد' : 'New')}
                    </div>
                  </div>

                  {/* Divider */}
                  <div style={{ width: '1px', height: '40px', backgroundColor: COLORS.borderLight }} />

                  {/* Sessions/Students */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}>
                      <FaUsers style={{ color: COLORS.orange, fontSize: '16px' }} />
                      <span style={{ fontSize: '20px', fontWeight: 700, color: COLORS.dark }}>
                        {toArabicNumerals(profilePopupTeacher.total_students || 0, isRTL)}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '2px' }}>
                      {isRTL ? 'طالب' : 'students'}
                    </div>
                  </div>
                </div>

                {/* Video Section */}
                {profilePopupTeacher.intro_video_url && (
                  <div style={{
                    marginBottom: '20px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    position: 'relative',
                    animation: 'popupItemFade 0.4s ease 0.3s both'
                  }}>
                    <video
                      src={profilePopupTeacher.intro_video_url}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        display: 'block',
                        backgroundColor: '#000'
                      }}
                      poster={profilePopupTeacher.profile_photo}
                      controls
                    />
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: COLORS.white,
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <FaPlay style={{ fontSize: '8px' }} />
                      {isRTL ? 'فيديو تعريفي' : 'Intro Video'}
                    </div>
                  </div>
                )}

                {/* Subjects Section */}
                <div style={{
                  marginBottom: '20px',
                  animation: 'popupItemFade 0.4s ease 0.35s both'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '10px'
                  }}>
                    <FaBook style={{ color: COLORS.orange, fontSize: '14px' }} />
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: COLORS.dark,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {isRTL ? 'المواد' : 'Subjects'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {(profilePopupTeacher.subjects || []).map((subject, idx) => {
                      const subjectTypes = profilePopupTeacher.subject_types?.[subject] || [];
                      return (
                        <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <span
                            style={{
                              background: 'linear-gradient(135deg, #FF7A1A 0%, #FF9A4D 100%)',
                              color: COLORS.white,
                              padding: '8px 16px',
                              borderRadius: '25px',
                              fontSize: '13px',
                              fontWeight: 500,
                              boxShadow: '0 2px 8px rgba(255, 122, 26, 0.3)',
                              transition: 'transform 0.2s ease',
                              cursor: 'default',
                              display: 'inline-block',
                              width: 'fit-content'
                            }}
                          >
                            {translateSubject(subject, isRTL)}
                          </span>
                          {subjectTypes.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', paddingLeft: '16px' }}>
                              {subjectTypes.map((type, typeIdx) => (
                                <span
                                  key={typeIdx}
                                  style={{
                                    background: '#FFF4E6',
                                    color: '#D97706',
                                    padding: '4px 12px',
                                    borderRadius: '15px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    border: '1px solid #FED7AA'
                                  }}
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Languages Section */}
                {profilePopupTeacher.languages && profilePopupTeacher.languages.length > 0 && (
                  <div style={{
                    marginBottom: '20px',
                    animation: 'popupItemFade 0.4s ease 0.4s both'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px'
                    }}>
                      <FaGlobe style={{ color: '#0EA5E9', fontSize: '14px' }} />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: COLORS.dark,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {isRTL ? 'اللغات' : 'Languages'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {profilePopupTeacher.languages.map((lang, idx) => {
                        // Handle both string format and object format
                        const languageName = typeof lang === 'string' ? lang : (lang.language || '');
                        const level = typeof lang === 'object' ? lang.level : '';

                        return (
                          <span
                            key={`lang-${idx}`}
                            style={{
                              background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)',
                              color: '#0369A1',
                              padding: '8px 16px',
                              borderRadius: '25px',
                              fontSize: '13px',
                              fontWeight: 500,
                              border: '1px solid #7DD3FC'
                            }}
                          >
                            {translateLanguage(languageName, isRTL)}
                            {level && ` (${level})`}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Bio Section */}
                {profilePopupTeacher.bio && (
                  <div style={{
                    marginBottom: '20px',
                    animation: 'popupItemFade 0.4s ease 0.45s both'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px'
                    }}>
                      <FaUser style={{ color: COLORS.textSecondary, fontSize: '13px' }} />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: COLORS.dark,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {isRTL ? 'نبذة عني' : 'About Me'}
                      </span>
                    </div>
                    <p style={{
                      margin: 0,
                      padding: '14px 16px',
                      backgroundColor: COLORS.gray,
                      borderRadius: '12px',
                      color: COLORS.textSecondary,
                      fontSize: '14px',
                      lineHeight: 1.7,
                      borderLeft: `3px solid ${COLORS.orange}`
                    }}>
                      {profilePopupTeacher.bio.length > 200
                        ? profilePopupTeacher.bio.substring(0, 200) + '...'
                        : profilePopupTeacher.bio}
                    </p>
                  </div>
                )}

                {/* Grade Levels Section */}
                {profilePopupTeacher.grade_levels && profilePopupTeacher.grade_levels.length > 0 && (
                  <div style={{
                    marginBottom: '16px',
                    animation: 'popupItemFade 0.4s ease 0.5s both'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px'
                    }}>
                      <FaGraduationCap style={{ color: '#8B5CF6', fontSize: '14px' }} />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: COLORS.dark,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {isRTL ? 'المراحل الدراسية' : 'Grade Levels'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {profilePopupTeacher.grade_levels.map((grade, idx) => (
                        <span
                          key={idx}
                          style={{
                            background: 'linear-gradient(135deg, #F3E8FF 0%, #E9D5FF 100%)',
                            color: '#7C3AED',
                            padding: '6px 14px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 500,
                            border: '1px solid #C4B5FD'
                          }}
                        >
                          {translateGrade(grade, isRTL)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location if available */}
                {profilePopupTeacher.location && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    color: COLORS.textSecondary,
                    fontSize: '13px',
                    marginBottom: '8px',
                    animation: 'popupItemFade 0.4s ease 0.55s both'
                  }}>
                    <FaMapMarkerAlt style={{ fontSize: '12px' }} />
                    <span>{profilePopupTeacher.location}</span>
                  </div>
                )}

                {/* Reviews Section */}
                {reviewCount > 0 && teacherReviews.length > 0 && (
                  <div style={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    animation: 'popupItemFade 0.4s ease 0.6s both'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px'
                    }}>
                      <FaComment style={{ color: COLORS.orange, fontSize: '14px' }} />
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: COLORS.dark,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {isRTL ? 'التقييمات' : 'Reviews'}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      paddingRight: '4px'
                    }}>
                      {teacherReviews.slice(0, 3).map((review, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '12px',
                            backgroundColor: COLORS.gray,
                            borderRadius: '12px',
                            border: '1px solid #E5E7EB'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '6px'
                          }}>
                            <div style={{ display: 'flex', gap: '2px' }}>
                              {[...Array(5)].map((_, i) => (
                                <FaStar
                                  key={i}
                                  size={12}
                                  color={i < (review.rating || 0) ? '#FBBF24' : '#E5E7EB'}
                                />
                              ))}
                            </div>
                            <span style={{
                              fontSize: '11px',
                              color: COLORS.textSecondary
                            }}>
                              {review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                          {review.comment && (
                            <p style={{
                              margin: 0,
                              fontSize: '13px',
                              color: COLORS.dark,
                              lineHeight: 1.5
                            }}>
                              {review.comment}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons - Fixed Footer */}
              <div style={{
                padding: '16px 24px 20px',
                borderTop: `1px solid ${COLORS.borderLight}`,
                background: 'linear-gradient(to top, white 0%, rgba(255,255,255,0.95) 100%)'
              }}>
                {/* Request Session Button */}
                <button
                  onClick={() => {
                    setProfilePopupTeacher(null);
                    navigate(`/teacher/${profilePopupTeacher.uuid_id}`);
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    background: 'linear-gradient(135deg, #FF7A1A 0%, #FF9A4D 100%)',
                    color: COLORS.white,
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(255, 122, 26, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(255, 122, 26, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(255, 122, 26, 0.4)';
                  }}
                >
                  <FaCalendarAlt style={{ fontSize: '14px' }} />
                  {isRTL ? 'طلب جلسة' : 'Request Session'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* CSS Animations */}
      <style>{`
        /* Price Range Slider Styling */
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
          height: 8px;
        }
        
        input[type="range"]::-webkit-slider-runnable-track {
          height: 8px;
          background: #E2E8F0;
          border-radius: 4px;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${COLORS.orange};
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(255, 122, 26, 0.4);
          border: 2px solid #FFFFFF;
          margin-top: -6px;
          transition: all 0.2s ease;
        }
        
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 10px rgba(255, 122, 26, 0.6);
        }
        
        input[type="range"]::-moz-range-track {
          height: 8px;
          background: #E2E8F0;
          border-radius: 4px;
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${COLORS.orange};
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(255, 122, 26, 0.4);
          border: 2px solid #FFFFFF;
          transition: all 0.2s ease;
        }
        
        input[type="range"]::-moz-range-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 10px rgba(255, 122, 26, 0.6);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes cardFadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes popupSlide {
          from { transform: scale(0.95) translateY(20px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes slideInVideo {
          from {
            opacity: 0;
            transform: translateX(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes playButtonPulse {
          0%, 100% {
            box-shadow: 0 4px 16px rgba(255, 122, 26, 0.5);
          }
          50% {
            box-shadow: 0 4px 20px rgba(255, 122, 26, 0.7), 0 0 0 8px rgba(255, 122, 26, 0.1);
          }
        }

        /* Premium Popup Animations */
        @keyframes popupOverlayFade {
          from {
            opacity: 0;
            backdrop-filter: blur(0px);
          }
          to {
            opacity: 1;
            backdrop-filter: blur(8px);
          }
        }
        @keyframes popupContentSlide {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(40px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes popupPhotoScale {
          0% {
            opacity: 0;
            transform: scale(0.5);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes popupItemFade {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Smooth scrollbar for popup */
        .popup-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .popup-scroll::-webkit-scrollbar-track {
          background: #F5F7FA;
          border-radius: 3px;
        }
        .popup-scroll::-webkit-scrollbar-thumb {
          background: #FF7A1A;
          border-radius: 3px;
        }
        .popup-scroll::-webkit-scrollbar-thumb:hover {
          background: #E86A0A;
        }
      `}</style>
    </div>
  );
};

export default TeacherBrowsePage;
